import React, { useState, useEffect, useCallback } from "react";
import { Link, useSearchParams, useNavigate } from "react-router-dom";
import {
  ComputerDesktopIcon,
  ExclamationTriangleIcon,
  CheckCircleIcon,
  XCircleIcon,
  BuildingOfficeIcon,
  UsersIcon,
  GlobeAltIcon,
  MapPinIcon,
  EyeIcon,
} from "@heroicons/react/24/outline";
import {
  PieChart,
  Pie,
  Cell,
  ResponsiveContainer,
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
} from "recharts";
import { apiService } from "../../services/api";
import { DashboardStats, Alert, SuperAdminStats, Tenant } from "../../types";
import LoadingSpinner from "../../components/common/LoadingSpinner";
import LogsViewer from "../../components/super-admin/LogsViewer";
import { useAuth } from "../../contexts/AuthContext";
import MachinesMap from "../../components/dashboard/MachinesMap";

export default function DashboardPage() {
  const {
    state: { user },
  } = useAuth();
  const [searchParams] = useSearchParams();
  const selectedMachineId = searchParams.get('machineId');

  // State declarations - MUST be at the top level of the component
  const [stats, setStats] = useState<DashboardStats | null>(null);
  const [superAdminStats, setSuperAdminStats] =
    useState<SuperAdminStats | null>(null);
  const [recentAlerts, setRecentAlerts] = useState<Alert[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState("");
  
  // Super admin map filter state
  const [selectedTenantId, setSelectedTenantId] = useState<string>("");
  const [tenants, setTenants] = useState<Tenant[]>([]);
  
  // Alert details modal state
  const [selectedAlert, setSelectedAlert] = useState<Alert | null>(null);
  const [showAlertDetails, setShowAlertDetails] = useState(false);
  const navigate = useNavigate();

  const loadDashboardData = useCallback(async () => {
    try {
      setIsLoading(true);
      setError("");

      if (user?.role === "super_admin") {
        const [overviewResponse, tenantsResponse] = await Promise.all([
          apiService.getSuperAdminOverview(),
          apiService.getTenants({ per_page: 100 }),
        ]);
        setSuperAdminStats(overviewResponse.data);
        setTenants(tenantsResponse.data);
      } else {
        const [statsResponse, alertsResponse] = await Promise.all([
          apiService.getDashboardStats(),
          apiService.getAlerts({ limit: 5 }),
        ]);

        // Map backend response to DashboardStats interface
        // Backend returns: { success: true, data: { machines: {...}, alerts: {...} } }
        // apiService.getDashboardStats() returns response.data from axios
        // So statsResponse = { success: true, data: { machines: {...}, alerts: {...} } }
        const backendStats = statsResponse?.data || statsResponse;
        
        // Convert values to numbers, handling string, null, or undefined values
        const mappedStats: DashboardStats = {
          totalMachines: parseInt(String(backendStats?.machines?.total ?? 0), 10) || 0,
          onlineMachines: parseInt(String(backendStats?.machines?.online ?? 0), 10) || 0,
          offlineMachines: parseInt(String(backendStats?.machines?.offline ?? 0), 10) || 0,
          warningMachines: parseInt(String(backendStats?.machines?.warning ?? 0), 10) || 0,
          totalAlerts: parseInt(String(backendStats?.alerts?.recent_count ?? 0), 10) || 0,
          openAlerts: parseInt(String(backendStats?.alerts?.recent_count ?? 0), 10) || 0,
          criticalAlerts: parseInt(String(backendStats?.alerts?.critical_count ?? 0), 10) || 0,
        };

        setStats(mappedStats);
        setRecentAlerts(alertsResponse.data.alerts);
      }
    } catch (err: any) {
      setError("Erro ao carregar dados do dashboard");
      console.error("Dashboard error:", err);
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  useEffect(() => {
    loadDashboardData();
  }, [loadDashboardData]);

  if (isLoading) {
    return (
      <div className="flex justify-center items-center h-64 dark:text-white">
        <LoadingSpinner size="lg" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-md p-4 dark:bg-red-900/20 dark:border-red-800">
        <div className="text-red-700 dark:text-red-400">{error}</div>
        <button
          onClick={loadDashboardData}
          className="mt-2 text-red-600 hover:text-red-800 underline dark:text-red-400 dark:hover:text-red-300"
        >
          Tentar novamente
        </button>
      </div>
    );
  }

  // --- Super Admin View ---
  if (user?.role === "super_admin" && superAdminStats) {
    const s = superAdminStats as any;

    return (
      <div className="space-y-4">
        <div className="flex justify-between items-center">
          <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
            Visão Geral do Sistema
          </h1>
          <div className="flex items-center space-x-2">
            <button
              onClick={loadDashboardData}
              className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
            >
              Atualizar
            </button>
          </div>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
          {/* Tenants Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <BuildingOfficeIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 truncate dark:text-gray-400">
                      Total de Empresas
                    </dt>
                    <dd className="text-base font-medium text-gray-900 dark:text-white">
                      {s.tenants?.total || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Machines Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ComputerDesktopIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 truncate dark:text-gray-400">
                      Total de Máquinas
                    </dt>
                    <dd className="text-base font-medium text-gray-900 dark:text-white">
                      {s.machines?.total || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Users Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <UsersIcon className="h-5 w-5 text-gray-400" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 truncate dark:text-gray-400">
                      Total de Usuários
                    </dt>
                    <dd className="text-base font-medium text-gray-900 dark:text-white">
                      {s.users?.total || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>

          {/* Alerts Card */}
          <div className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800">
            <div className="p-3">
              <div className="flex items-center">
                <div className="flex-shrink-0">
                  <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
                </div>
                <div className="ml-3 w-0 flex-1">
                  <dl>
                    <dt className="text-xs font-medium text-gray-500 truncate dark:text-gray-400">
                      Alertas Abertos
                    </dt>
                    <dd className="text-base font-medium text-gray-900 dark:text-white">
                      {s.alerts?.open || 0}
                    </dd>
                  </dl>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Recent Activity / Top Tenants */}
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
          <div className="bg-white shadow rounded-lg p-4 dark:bg-gray-800">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Top Inquilinos (Máquinas)
            </h3>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {s.top_tenants?.map((tenant: any) => (
                <li key={tenant.id} className="py-2 flex justify-between text-xs">
                  <span className="text-gray-700 dark:text-gray-300">
                    {tenant.name}
                  </span>
                  <span className="font-medium text-gray-900 dark:text-white">
                    {tenant.machines_count} máquinas
                  </span>
                </li>
              ))}
            </ul>
          </div>

          <div className="bg-white shadow rounded-lg p-4 dark:bg-gray-800">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Alertas Críticos Recentes
            </h3>
            <ul className="divide-y divide-gray-200 dark:divide-gray-700">
              {s.recent_activity?.alerts?.map((alert: any) => (
                <li key={alert.id} className="py-2">
                  <div className="flex justify-between text-xs">
                    <span className="text-red-600 font-medium">
                      {alert.title}
                    </span>
                    <span className="text-gray-500 dark:text-gray-400">
                      {new Date(alert.created_at).toLocaleDateString()}
                    </span>
                  </div>
                  <p className="text-xs text-gray-600 dark:text-gray-400 mt-0.5">
                    {alert.tenant_name} - {alert.machine_name}
                  </p>
                </li>
              ))}
              {(!s.recent_activity?.alerts ||
                s.recent_activity.alerts.length === 0) && (
                <li className="py-2 text-xs text-gray-500 dark:text-gray-400">
                  Nenhum alerta crítico recente.
                </li>
              )}
            </ul>
          </div>
        </div>

        {/* Machines Map with Tenant Filter */}
        <div className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800">
          <div className="px-3 py-4 sm:p-4">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-medium text-gray-900 dark:text-white">
                Localização das Máquinas
              </h3>
              <select
                value={selectedTenantId}
                onChange={(e) => setSelectedTenantId(e.target.value)}
                className="block w-48 border border-gray-300 rounded shadow-sm py-1.5 px-2 text-xs focus:outline-none focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-700 dark:border-gray-600 dark:text-white"
              >
                <option value="">Todas as Empresas</option>
                {tenants.map((tenant) => (
                  <option key={tenant.id} value={tenant.id}>
                    {tenant.name}
                  </option>
                ))}
              </select>
            </div>
            <MachinesMap tenantId={selectedTenantId || undefined} />
          </div>
        </div>

        {/* Logs Viewer Section */}
        <div className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800">
          <div className="px-3 py-4 sm:p-4">
            <LogsViewer />
          </div>
        </div>
      </div>
    );
  }

  // --- Tenant Admin / User View ---
  if (!stats) {
    return <div className="dark:text-white">Dados não disponíveis</div>;
  }

  // Ensure values are valid numbers and create chart data
  const machineStatusData = [
    { name: "Online", value: Number(stats.onlineMachines) || 0, color: "#10B981" },
    { name: "Offline", value: Number(stats.offlineMachines) || 0, color: "#EF4444" },
    { name: "Aviso", value: Number(stats.warningMachines) || 0, color: "#F59E0B" },
  ];
  
  // Filter out zero values for the chart (only show segments with data)
  const chartData = machineStatusData.filter(item => item.value > 0);
  
  // Show chart if there are any machines (even if all are offline/online/warning)
  const hasMachineData = stats.totalMachines > 0;

  const alertsData = [
    { name: "Críticos", value: stats.criticalAlerts, color: "#EF4444" },
    {
      name: "Abertos",
      value: stats.openAlerts - stats.criticalAlerts,
      color: "#F59E0B",
    },
    { name: "Total", value: stats.totalAlerts, color: "#6B7280" },
  ];

  const getSeverityColor = (severity: string) => {
    switch (severity) {
      case "critical":
        return "text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400";
      case "high":
        return "text-orange-600 bg-orange-100 dark:bg-orange-900/30 dark:text-orange-400";
      case "medium":
        return "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400";
      case "low":
        return "text-blue-600 bg-blue-100 dark:bg-blue-900/30 dark:text-blue-400";
      default:
        return "text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400";
    }
  };

  const getAlertTypeIcon = (type: string) => {
    switch (type) {
      case "hardware_change":
        return <ComputerDesktopIcon className="w-4 h-4" />;
      case "blocked_site":
        return <GlobeAltIcon className="w-4 h-4" />;
      case "offline":
        return <XCircleIcon className="w-4 h-4" />;
      case "location_change":
        return <MapPinIcon className="w-4 h-4" />;
      default:
        return <ExclamationTriangleIcon className="w-4 h-4" />;
    }
  };

  const getAlertTypeName = (type: string) => {
    switch (type) {
      case "hardware_change":
        return "Mudança de Hardware";
      case "blocked_site":
        return "Site Bloqueado";
      case "offline":
        return "Máquina Offline";
      case "location_change":
        return "Mudança de Localização";
      default:
        return type;
    }
  };

  const translateStatus = (status: string) => {
    switch (status) {
      case "open":
        return "Aberto";
      case "acknowledged":
        return "Confirmado";
      case "resolved":
        return "Resolvido";
      default:
        return status;
    }
  };

  const translateSeverity = (severity: string) => {
    switch (severity) {
      case "critical":
        return "Crítico";
      case "high":
        return "Alto";
      case "medium":
        return "Médio";
      case "low":
        return "Baixo";
      default:
        return severity;
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case "open":
        return "text-red-600 bg-red-100 dark:bg-red-900/30 dark:text-red-400";
      case "acknowledged":
        return "text-yellow-600 bg-yellow-100 dark:bg-yellow-900/30 dark:text-yellow-400";
      case "resolved":
        return "text-green-600 bg-green-100 dark:bg-green-900/30 dark:text-green-400";
      default:
        return "text-gray-600 bg-gray-100 dark:bg-gray-700 dark:text-gray-400";
    }
  };

  const openAlertDetails = (alert: Alert) => {
    setSelectedAlert(alert);
    setShowAlertDetails(true);
  };

  return (
    <div className="space-y-4">
      <div className="flex justify-between items-center">
        <h1 className="text-xl font-semibold text-gray-900 dark:text-white">
          Dashboard
        </h1>
        <div className="flex items-center space-x-2">
          <button
            onClick={loadDashboardData}
            className="px-3 py-1.5 text-xs font-medium text-gray-700 bg-white border border-gray-300 rounded hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 dark:bg-gray-800 dark:text-gray-300 dark:border-gray-600 dark:hover:bg-gray-700"
          >
            Atualizar
          </button>
        </div>
      </div>

      {/* Stats Cards - Now clickable! */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Link to="/machines" className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800 hover:shadow-md transition-shadow duration-200 cursor-pointer">
          <div className="p-3">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ComputerDesktopIcon className="h-5 w-5 text-gray-400" />
              </div>
              <div className="ml-3 w-0 flex-1">
                <dl>
                  <dt className="text-xs font-medium text-gray-500 truncate dark:text-gray-400">
                    Total de Máquinas
                  </dt>
                  <dd className="text-base font-medium text-gray-900 dark:text-white">
                    {stats.totalMachines}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/machines?status=online" className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800 hover:shadow-md transition-shadow duration-200 cursor-pointer">
          <div className="p-3">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <CheckCircleIcon className="h-5 w-5 text-green-400" />
              </div>
              <div className="ml-3 w-0 flex-1">
                <dl>
                  <dt className="text-xs font-medium text-gray-500 truncate dark:text-gray-400">
                    Máquinas Online
                  </dt>
                  <dd className="text-base font-medium text-gray-900 dark:text-white">
                    {stats.onlineMachines}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/machines?status=offline" className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800 hover:shadow-md transition-shadow duration-200 cursor-pointer">
          <div className="p-3">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <XCircleIcon className="h-5 w-5 text-red-400" />
              </div>
              <div className="ml-3 w-0 flex-1">
                <dl>
                  <dt className="text-xs font-medium text-gray-500 truncate dark:text-gray-400">
                    Máquinas Offline
                  </dt>
                  <dd className="text-base font-medium text-gray-900 dark:text-white">
                    {stats.offlineMachines}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>

        <Link to="/alerts" className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800 hover:shadow-md transition-shadow duration-200 cursor-pointer">
          <div className="p-3">
            <div className="flex items-center">
              <div className="flex-shrink-0">
                <ExclamationTriangleIcon className="h-5 w-5 text-yellow-400" />
              </div>
              <div className="ml-3 w-0 flex-1">
                <dl>
                  <dt className="text-xs font-medium text-gray-500 truncate dark:text-gray-400">
                    Alertas Abertos
                  </dt>
                  <dd className="text-base font-medium text-gray-900 dark:text-white">
                    {stats.openAlerts}
                  </dd>
                </dl>
              </div>
            </div>
          </div>
        </Link>
      </div>

      {/* Machines Map */}
      <div className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800">
        <div className="px-3 py-4 sm:p-4">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
            Localização das Máquinas
          </h3>
          <MachinesMap initialMachineId={selectedMachineId || undefined} />
        </div>
      </div>

      {/* Charts */}
      <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
        <div className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800">
          <div className="px-3 py-4 sm:p-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Status das Máquinas
            </h3>
            <div className="h-48">
              {hasMachineData ? (
                <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                  <PieChart>
                    <Pie
                      data={chartData}
                      cx="50%"
                      cy="50%"
                      labelLine={false}
                      label={({ name, value }) => `${name}: ${value}`}
                      outerRadius={60}
                      fill="#8884d8"
                      dataKey="value"
                    >
                      {chartData.map((entry, index) => (
                        <Cell key={`cell-${index}`} fill={entry.color} />
                      ))}
                    </Pie>
                    <Tooltip
                      contentStyle={{
                        backgroundColor: "#ffffff",
                        border: "1px solid #e5e7eb",
                        borderRadius: "0.375rem",
                        color: "#1f2937",
                        boxShadow:
                          "0 4px 6px -1px rgba(0, 0, 0, 0.1), 0 2px 4px -1px rgba(0, 0, 0, 0.06)",
                      }}
                      itemStyle={{ color: "#1f2937" }}
                      labelStyle={{ color: "#1f2937", fontWeight: 600 }}
                    />
                  </PieChart>
                </ResponsiveContainer>
              ) : (
                <div className="flex items-center justify-center h-full text-gray-500 dark:text-gray-400">
                  <p>Nenhuma máquina cadastrada</p>
                </div>
              )}
            </div>
          </div>
        </div>

        <div className="bg-white overflow-hidden shadow rounded-lg dark:bg-gray-800">
          <div className="px-3 py-4 sm:p-4">
            <h3 className="text-sm font-medium text-gray-900 dark:text-white mb-3">
              Alertas por Severidade
            </h3>
            <div className="h-48">
              <ResponsiveContainer width="100%" height="100%" minWidth={0} minHeight={0}>
                <BarChart data={alertsData}>
                  <CartesianGrid strokeDasharray="3 3" stroke="#374151" />
                  <XAxis dataKey="name" stroke="#9CA3AF" />
                  <YAxis stroke="#9CA3AF" />
                  <Tooltip
                    contentStyle={{
                      backgroundColor: "#1f2937",
                      border: "none",
                      color: "#fff",
                    }}
                  />
                  <Bar dataKey="value" fill="#3B82F6" />
                </BarChart>
              </ResponsiveContainer>
            </div>
          </div>
        </div>
      </div>

      {/* Recent Alerts */}
      <div className="bg-white shadow overflow-hidden sm:rounded-lg dark:bg-gray-800">
        <div className="px-3 py-3 sm:px-4 flex justify-between items-center">
          <h3 className="text-sm font-medium text-gray-900 dark:text-white">
            Alertas Recentes
          </h3>
          <Link
            to="/alerts"
            className="text-xs font-medium text-blue-600 hover:text-blue-500 dark:text-blue-400 dark:hover:text-blue-300"
          >
            Ver todos
          </Link>
        </div>
        <ul className="divide-y divide-gray-200 dark:divide-gray-700">
          {recentAlerts.length === 0 ? (
            <li className="px-3 py-3 text-center text-xs text-gray-500 dark:text-gray-400">
              Nenhum alerta recente
            </li>
          ) : (
            recentAlerts.map((alert) => (
              <li key={alert.id} className="px-3 py-2.5">
                <div className="flex items-center space-x-3">
                  <div className="flex-shrink-0">
                    <div
                      className={`p-1.5 rounded-full ${getSeverityColor(
                        alert.severity
                      )}`}
                    >
                      {getAlertTypeIcon(alert.type)}
                    </div>
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center space-x-2">
                      <p className="text-xs font-medium text-gray-900 dark:text-white truncate">
                        {alert.title}
                      </p>
                      {(alert.machine_name || alert.machine_branch) && (
                        <span className="text-xs text-gray-500 dark:text-gray-400">
                          • {alert.machine_name || "Máquina desconhecida"}
                          {alert.machine_branch && ` - ${alert.machine_branch}`}
                        </span>
                      )}
                    </div>
                    <p className="text-xs text-gray-500 dark:text-gray-400 truncate mt-0.5">
                      {alert.description}
                    </p>
                  </div>
                  <div className="flex items-center space-x-1.5">
                    <div className="flex-shrink-0 text-xs text-gray-500 dark:text-gray-400">
                      {new Date(alert.created_at).toLocaleDateString("pt-BR")}
                    </div>
                    <div className="flex-shrink-0">
                      <span
                        className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${getSeverityColor(
                          alert.severity
                        )}`}
                      >
                        {translateSeverity(alert.severity)}
                      </span>
                    </div>
                    <button
                      onClick={() => openAlertDetails(alert)}
                      className="inline-flex items-center px-1.5 py-0.5 border border-gray-300 dark:border-gray-600 text-xs font-medium rounded text-gray-700 dark:text-gray-200 bg-white dark:bg-gray-700 hover:bg-gray-50 dark:hover:bg-gray-600"
                      title="Ver detalhes do alerta"
                    >
                      <EyeIcon className="w-3 h-3 mr-0.5" />
                      Ver
                    </button>
                  </div>
                </div>
              </li>
            ))
          )}
        </ul>
      </div>

      {/* Alert Details Modal */}
      {showAlertDetails && selectedAlert && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 overflow-y-auto h-full w-full z-50">
          <div className="relative top-20 mx-auto p-5 border w-11/12 md:w-3/4 lg:w-1/2 shadow-lg rounded-md bg-white dark:bg-gray-800 dark:border-gray-700">
            <div className="mt-3">
              <div className="flex items-center justify-between mb-4">
                <h3 className="text-lg font-medium text-gray-900 dark:text-white">
                  Detalhes do Alerta
                </h3>
                <button
                  onClick={() => setShowAlertDetails(false)}
                  className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                >
                  <XCircleIcon className="w-6 h-6" />
                </button>
              </div>

              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Título
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {selectedAlert.title}
                  </p>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                    Descrição
                  </label>
                  <p className="mt-1 text-sm text-gray-900 dark:text-white">
                    {selectedAlert.description}
                  </p>
                </div>

                {(selectedAlert.machine_name || selectedAlert.machine_branch) && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Máquina
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {selectedAlert.machine_name || "Máquina desconhecida"}
                      {selectedAlert.machine_branch && ` - ${selectedAlert.machine_branch}`}
                    </p>
                  </div>
                )}

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Tipo
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {getAlertTypeName(selectedAlert.type)}
                    </p>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Severidade
                    </label>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getSeverityColor(
                        selectedAlert.severity
                      )}`}
                    >
                      {translateSeverity(selectedAlert.severity)}
                    </span>
                  </div>
                </div>

                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Status
                    </label>
                    <span
                      className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(
                        selectedAlert.status
                      )}`}
                    >
                      {translateStatus(selectedAlert.status)}
                    </span>
                  </div>
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Criado em
                    </label>
                    <p className="mt-1 text-sm text-gray-900 dark:text-white">
                      {new Date(selectedAlert.created_at).toLocaleString("pt-BR")}
                    </p>
                  </div>
                </div>

                {selectedAlert.data && (
                  <div>
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Dados Adicionais
                    </label>
                    <pre className="mt-1 text-xs bg-gray-50 dark:bg-gray-900 p-3 rounded-md overflow-x-auto text-gray-800 dark:text-gray-300">
                      {JSON.stringify(selectedAlert.data, null, 2)}
                    </pre>
                  </div>
                )}
              </div>

              <div className="mt-6 flex justify-end space-x-3">
                {selectedAlert.machine_id && (
                  <button
                    onClick={() => {
                      setShowAlertDetails(false);
                      navigate(`/machines/${selectedAlert.machine_id}`);
                    }}
                    className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-md hover:bg-blue-700 dark:bg-blue-500 dark:hover:bg-blue-600"
                  >
                    <ComputerDesktopIcon className="w-4 h-4 inline mr-2" />
                    Ver Máquina
                  </button>
                )}
                <button
                  onClick={() => setShowAlertDetails(false)}
                  className="px-4 py-2 bg-gray-300 dark:bg-gray-600 text-gray-700 dark:text-gray-200 text-sm font-medium rounded-md hover:bg-gray-400 dark:hover:bg-gray-500"
                >
                  Fechar
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
